/**
 * SearchableDropdown — Reusable searchable dropdown component
 * Renders a text input that opens a filterable dropdown list.
 * Uses a portal to render the dropdown at document body level,
 * so it is never clipped by parent overflow containers.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';

export interface SearchableDropdownOption {
  value: string;
  label: string;
  /** If true, renders as a non-selectable group header */
  isGroupHeader?: boolean;
}

interface SearchableDropdownProps {
  options: SearchableDropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  /** Called when the search text changes — use to trigger async fetches and merge results into options */
  onSearchChange?: (text: string) => void;
  /** Shown when the search matches nothing */
  noMatchesText?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  onSearchChange,
  noMatchesText = 'No matches',
}) => {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Position the dropdown below the input
  const updatePosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // Update position when dropdown opens and on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close on outside click (check both container and portal dropdown)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Deduplicate options by value (skip group headers from dedup)
  const uniqueOptions = options.filter((opt, i, arr) =>
    opt.isGroupHeader || arr.findIndex(x => x.value === opt.value) === i
  );

  // Filter options by search text (substring match), preserving group headers
  const filteredOptions = useMemo(() => {
    if (searchText.trim().length === 0) return uniqueOptions;
    const term = searchText.trim().toLowerCase();
    // Include a group header if any following non-header option matches
    const result: typeof uniqueOptions = [];
    let pendingHeader: typeof uniqueOptions[0] | null = null;
    for (const opt of uniqueOptions) {
      if (opt.isGroupHeader) {
        pendingHeader = opt;
      } else if (opt.label.toLowerCase().includes(term)) {
        if (pendingHeader) { result.push(pendingHeader); pendingHeader = null; }
        result.push(opt);
      }
    }
    return result;
  }, [uniqueOptions, searchText]);

  // Display text: when open show search text, when closed show selected label or placeholder
  const selectedLabel = value
    ? (uniqueOptions.find(o => o.value === value)?.label || value)
    : '';

  const handleSelect = (optValue: string) => {
    onChange(optValue);
    setSearchText('');
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchText('');
  };

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 9999,
      }}
      className={`max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg ${dropdownClassName}`}
    >
      {/* "All" / clear option */}
      <button
        onClick={() => handleSelect('')}
        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${!value ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
      >
        {placeholder}
      </button>
      {filteredOptions.map((opt, idx) => opt.isGroupHeader ? (
        <div
          key={`header-${idx}`}
          className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider border-t border-gray-100 dark:border-gray-700 first:border-t-0"
        >
          {opt.label}
        </div>
      ) : (
        <button
          key={opt.value}
          onClick={() => handleSelect(opt.value)}
          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${value === opt.value ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
        >
          {opt.label}
        </button>
      ))}
      {filteredOptions.length === 0 && (
        <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">{noMatchesText}</div>
      )}
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={isOpen ? searchText : selectedLabel}
        onChange={e => { setSearchText(e.target.value); setIsOpen(true); onSearchChange?.(e.target.value); }}
        onFocus={() => { setIsOpen(true); setSearchText(''); updatePosition(); }}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${!value && !isOpen ? 'text-gray-500 dark:text-gray-400' : ''} ${inputClassName}`}
      />
      {value && !isOpen && (
        <button
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-base leading-none"
        >
          ×
        </button>
      )}
      {ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default SearchableDropdown;
